import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Checkbox,
  FormControlLabel,
  MenuItem,
} from '@mui/material';
import { MdDownload, MdClose, MdVerified } from 'react-icons/md';
import theme from '../theme';
import api, { API_BASE_URL } from '../../utils/api';
import { getAccessToken } from '../../utils/auth';
import {
  patchFollowUp,
  upsertManualClean,
  createVerification,
  fetchEvidencePackDetail,
} from '../../services/clientReportsApi';

const COLORS = theme.colors;

const COVERAGE_STATUS_LABEL = {
  adequate: 'Adequate',
  shadowed: 'Shadowed (low accuracy)',
  unreachable: 'Unreachable',
  unknown: 'Unknown',
  inadequate: 'Low accuracy',
  incomplete_cycle: 'Incomplete cycle',
};

const METHOD_OPTIONS = [
  { value: 'direct_observation', label: 'Direct observation' },
  { value: 'atp', label: 'ATP' },
  { value: 'fluorescent_marker', label: 'Fluorescent marker' },
  { value: 'agar_slide', label: 'Agar slide' },
  { value: 'swab_culture', label: 'Swab culture' },
];

const METHOD_LABEL = Object.fromEntries(METHOD_OPTIONS.map((m) => [m.value, m.label]));

const DEFAULT_CLEAN_STEPS = [
  { key: 'surfaces', label: 'High-touch surfaces wiped with approved disinfectant' },
  { key: 'floors', label: 'Floors cleaned per housekeeping protocol' },
  { key: 'waste', label: 'Waste removed / linens handled per protocol' },
  { key: 'visual', label: 'Visual inspection satisfactory before UV-C adjunct' },
];

function formatDuration(seconds) {
  if (seconds == null || !Number.isFinite(Number(seconds))) return '—';
  const m = Math.round(Number(seconds) / 60);
  return `${m} min`;
}

async function downloadPackPdf(historyId, email) {
  const token = getAccessToken();
  const url = `${API_BASE_URL}/client/reports/packs/${encodeURIComponent(
    historyId
  )}/pdf?email=${encodeURIComponent(email)}`;
  const res = await fetch(url, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to download PDF');
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = `Spotless_Evidence_${historyId}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   historyId: string | null,
 *   email: string,
 *   onUpdated?: () => void,
 *   adminView?: boolean,
 *   canWriteReports?: boolean,
 *   canExport?: boolean,
 *   canWriteManualClean?: boolean,
 *   canWriteVerification?: boolean,
 *   canWriteFollowUps?: boolean,
 * }} props
 */
export default function EvidencePackDetailDrawer({
  open,
  onClose,
  historyId,
  email,
  onUpdated,
  adminView,
  canWriteReports = false,
  canExport = false,
  canWriteManualClean = false,
  canWriteVerification = false,
  canWriteFollowUps = false,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [signerRole, setSignerRole] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [cleanerName, setCleanerName] = useState('');
  const [cleanerRole, setCleanerRole] = useState('');
  const [stepDone, setStepDone] = useState({});
  const [verifyMethod, setVerifyMethod] = useState('atp');
  const [verifyInstrument, setVerifyInstrument] = useState('default');
  const [verifyValue, setVerifyValue] = useState('');
  const [verifyLocation, setVerifyLocation] = useState('');
  const [verifyNotes, setVerifyNotes] = useState('');
  const [verifyPassed, setVerifyPassed] = useState('');
  const [cleanNotes, setCleanNotes] = useState('');
  const [protocolName, setProtocolName] = useState('');
  const [pdfStale, setPdfStale] = useState(false);
  const [assessorFeedback, setAssessorFeedback] = useState('');
  const [surveyFramework, setSurveyFramework] = useState('');

  React.useEffect(() => {
    if (!open || !historyId || !email) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchEvidencePackDetail(historyId, email);
        if (!cancelled) {
          if (data?.success) {
            setDetail(data);
            const mc = data.manualClean;
            setCleanerName(mc?.cleanerName || '');
            setCleanerRole(mc?.cleanerRole || '');
            setCleanNotes(mc?.notes || '');
            setProtocolName(mc?.protocolName || '');
            const map = {};
            (mc?.steps || []).forEach((s) => {
              map[s.key] = Boolean(s.completed);
            });
            setStepDone(map);
            setPdfStale(false);
          } else setError(data?.message || 'Unable to load pack');
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load evidence pack');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, historyId, email]);

  const cycle = detail?.cycle;
  const pack = detail?.pack;
  const coverage = detail?.coverage;
  const followUpTasks = detail?.followUpTasks || [];
  const standards = detail?.standardsMap || [];
  const swabs = detail?.swabs || [];
  const manualClean = detail?.manualClean;
  const verifications = detail?.verifications || [];
  const mapUrl = detail?.history?.result_map_image || cycle?.mapImageUrl;
  const incompleteRecord = Boolean(detail?.incompleteRecord);
  const motionEvents = detail?.history?.motionDetectionTimes || [];
  const allowManualClean = !adminView && canWriteManualClean;
  const allowVerification = !adminView && canWriteVerification;
  const allowFollowUpWrite = !adminView && canWriteFollowUps;
  const allowGenerate = !adminView && canWriteReports;
  const allowDownload = !adminView && canExport;
  const allowSignOff = !adminView && canWriteReports;

  const statusChipColor = (status) => {
    if (status === 'adequate') return { bg: '#ecfdf5', color: COLORS.accentSuccess };
    if (['inadequate', 'incomplete_cycle', 'shadowed', 'unreachable'].includes(status)) {
      return { bg: '#fffbeb', color: COLORS.accentWarning };
    }
    return { bg: COLORS.bgSecondary, color: COLORS.textSecondary };
  };

  const refreshDetail = async () => {
    const data = await fetchEvidencePackDetail(historyId, email);
    if (data?.success) {
      setDetail(data);
      const mc = data.manualClean;
      setCleanerName(mc?.cleanerName || '');
      setCleanerRole(mc?.cleanerRole || '');
      setCleanNotes(mc?.notes || '');
      setProtocolName(mc?.protocolName || '');
      const map = {};
      (mc?.steps || []).forEach((s) => {
        map[s.key] = Boolean(s.completed);
      });
      setStepDone(map);
    }
    onUpdated?.();
  };

  const handleFollowUpStatus = async (taskId, status, noteText) => {
    setBusy(`fu-${taskId}`);
    setError('');
    try {
      await patchFollowUp(taskId, email, { status, verificationNote: noteText || '' });
      await refreshDetail();
    } catch (e) {
      setError(e?.response?.data?.message || 'Follow-up update failed');
    } finally {
      setBusy('');
    }
  };

  const handleSaveManualClean = async (markComplete) => {
    if (!allowManualClean) return;
    const name = String(cleanerName || '').trim();
    if (!name) {
      setError('Cleaner name is required before saving manual clean.');
      return;
    }
    const steps = (manualClean?.steps?.length ? manualClean.steps : DEFAULT_CLEAN_STEPS).map(
      (s) => ({
        key: s.key,
        label: s.label,
        completed: Boolean(stepDone[s.key]),
      })
    );
    if (markComplete && !steps.every((s) => s.completed)) {
      setError('Mark all checklist steps complete before marking the manual clean complete.');
      return;
    }
    setBusy('manual');
    setError('');
    try {
      await upsertManualClean(historyId, email, {
        cleanerName: name,
        cleanerRole: String(cleanerRole || '').trim(),
        protocolName: String(protocolName || '').trim() || undefined,
        notes: String(cleanNotes || '').trim() || undefined,
        steps,
        status: markComplete || steps.every((s) => s.completed) ? 'complete' : 'incomplete',
      });
      setPdfStale(true);
      await refreshDetail();
    } catch (e) {
      setError(e?.response?.data?.message || 'Manual clean save failed');
    } finally {
      setBusy('');
    }
  };

  const handleAddVerification = async () => {
    if (!allowVerification) return;
    const location = String(verifyLocation || '').trim();
    const notes = String(verifyNotes || '').trim();
    const isObservation = verifyMethod === 'direct_observation';
    const isCulture = verifyMethod === 'agar_slide' || verifyMethod === 'swab_culture';
    const hasNumeric = verifyValue !== '' && Number.isFinite(Number(verifyValue));

    if (!isObservation && !location) {
      setError('Location / surface is required for ATP, marker, and culture verifications.');
      return;
    }
    if (isObservation && !notes && !hasNumeric) {
      setError('Add observation notes (or a numeric score) for direct observation.');
      return;
    }
    if ((isObservation || isCulture) && !hasNumeric && verifyPassed === '') {
      setError('Select pass or fail for observation and culture results.');
      return;
    }
    if (!isObservation && !isCulture && !hasNumeric) {
      setError('Enter a numeric raw value for this verification method.');
      return;
    }

    setBusy('verify');
    setError('');
    try {
      const body = {
        method: verifyMethod,
        instrumentId: verifyInstrument || 'default',
        locationLabel: location,
        notes: notes || undefined,
      };
      if (hasNumeric) body.rawValue = Number(verifyValue);
      if (isObservation || isCulture) {
        body.rawText = notes || undefined;
        if (verifyPassed === 'true') body.passed = true;
        else if (verifyPassed === 'false') body.passed = false;
      }
      await createVerification(historyId, email, body);
      setVerifyValue('');
      setVerifyLocation('');
      setVerifyNotes('');
      setVerifyPassed('');
      setPdfStale(true);
      await refreshDetail();
    } catch (e) {
      setError(e?.response?.data?.message || 'Verification save failed');
    } finally {
      setBusy('');
    }
  };

  const handleGenerate = async () => {
    if (!allowGenerate) return;
    setBusy('generate');
    setError('');
    try {
      await api.post(
        `/client/reports/packs/${encodeURIComponent(historyId)}/generate?email=${encodeURIComponent(
          email
        )}`,
        { email }
      );
      setPdfStale(false);
      await refreshDetail();
    } catch (e) {
      setError(e?.response?.data?.message || 'Generate failed');
    } finally {
      setBusy('');
    }
  };

  const handleDownload = async () => {
    if (!allowDownload) return;
    if (pdfStale) {
      setError(
        'Regenerate the pack PDF first so downloads include the latest manual clean and verification evidence.'
      );
      return;
    }
    setBusy('download');
    setError('');
    try {
      await downloadPackPdf(historyId, email);
      onUpdated?.();
    } catch (e) {
      setError(e.message || 'Download failed');
    } finally {
      setBusy('');
    }
  };

  const handleSignOff = async () => {
    if (!allowSignOff) return;
    if (!signerName.trim() || !signerRole.trim()) {
      setError('Signer name and role are required');
      return;
    }
    setBusy('sign');
    setError('');
    try {
      const body = {
        email,
        signerName: signerName.trim(),
        signerRole: signerRole.trim(),
        note: note.trim(),
      };
      if (assessorFeedback.trim()) {
        body.assessorFeedback = assessorFeedback.trim();
        body.surveyFramework = surveyFramework || 'other';
        body.surveyAcceptedAt = new Date().toISOString();
      }
      await api.post(
        `/client/reports/packs/${encodeURIComponent(historyId)}/sign-off?email=${encodeURIComponent(
          email
        )}`,
        body
      );
      await refreshDetail();
      setSignerName('');
      setSignerRole('');
      setNote('');
      setAssessorFeedback('');
      setSurveyFramework('');
      setPdfStale(false);
      onUpdated?.();
    } catch (e) {
      setError(e?.response?.data?.message || 'Sign-off failed');
    } finally {
      setBusy('');
    }
  };

  const formatLatency = (ms) => {
    if (ms == null || !Number.isFinite(Number(ms))) return null;
    const n = Number(ms);
    if (n < 1000) return `${Math.round(n)} ms`;
    const sec = Math.round(n / 100) / 10;
    if (sec < 60) return `${sec}s`;
    return `${Math.round(sec / 60)} min ${Math.round(sec % 60)}s`;
  };

  const sectionLabelSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: COLORS.textSecondary,
    mb: 1,
  };

  const checklistSteps =
    manualClean?.steps?.length > 0 ? manualClean.steps : DEFAULT_CLEAN_STEPS;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 480 },
          bgcolor: COLORS.bgSecondary,
        },
      }}
    >
      <Box
        sx={{
          background: theme.gradients.primary,
          px: 2.5,
          py: 2,
          color: '#fff',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box>
              <Typography sx={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em' }}>
                Evidence pack
              </Typography>
              <Typography sx={{ fontSize: 13, mt: 0.35, opacity: 0.9 }}>
                {cycle?.roomName || '—'} · {cycle?.status || '—'}
                {detail?.riskTier ? ` · ${detail.riskTier}` : ''}
              </Typography>
              <Typography sx={{ fontSize: 11, mt: 0.75, opacity: 0.85, maxWidth: 360, lineHeight: 1.4 }}>
                Per-cycle hygiene record for this turnover: UV-C cycle + manual clean + verification.
                Room-scoped only — no patient identity. Not a multi-cycle committee pack.
              </Typography>
        </Box>
        <Button
          onClick={onClose}
          sx={{
            minWidth: 36,
            width: 36,
            height: 36,
            color: '#fff',
            bgcolor: 'rgba(255,255,255,0.15)',
            borderRadius: '10px',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
          }}
        >
          <MdClose size={20} />
        </Button>
      </Box>

      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} sx={{ color: COLORS.brandPrimary }} />
          </Box>
        ) : (
          <>
            {error ? (
              <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: `${theme.radius.md}px` }}>
                {error}
              </Alert>
            ) : null}

            {pdfStale ? (
              <Alert severity="warning" sx={{ borderRadius: `${theme.radius.md}px`, fontSize: 12 }}>
                Evidence changed since the last PDF generate — regenerate the pack PDF before
                download or sign-off so exports include the latest manual clean / verification.
              </Alert>
            ) : null}

            {detail?.disclaimer ? (
              <Alert severity="info" sx={{ borderRadius: `${theme.radius.md}px`, fontSize: 12 }}>
                {detail.disclaimer}
              </Alert>
            ) : null}

            {incompleteRecord ? (
              <Alert severity="warning" sx={{ borderRadius: `${theme.radius.md}px` }}>
                Incomplete record — UV-C cycle without completed manual clean.
                {detail?.uvAdjunctDisclaimer ? ` ${detail.uvAdjunctDisclaimer}` : ''}
              </Alert>
            ) : null}

            {cycle ? (
              <Box
                sx={{
                  p: 2,
                  borderRadius: `${theme.radius.md}px`,
                  bgcolor: '#fff',
                  border: `1px solid ${COLORS.borderDefault}`,
                  boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
                }}
              >
                <Typography sx={sectionLabelSx}>UV-C CYCLE</Typography>
                <Typography sx={{ fontSize: 13, color: COLORS.textPrimary, mb: 0.5 }}>
                  Date:{' '}
                  {cycle.cycleDate ? new Date(cycle.cycleDate).toLocaleString('en-GB') : '—'}
                </Typography>
                <Typography sx={{ fontSize: 13, color: COLORS.textPrimary, mb: 0.5 }}>
                  Duration: {formatDuration(cycle.disinfectionTimeTakenSeconds)} · UV exposure
                  (proxy): {formatDuration(cycle.uvLightUsageInSeconds)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: COLORS.textSecondary, mb: 0.5 }}>
                  Runtime seconds — not radiometric dose (mJ/cm²).
                </Typography>
                <Typography sx={{ fontSize: 13, color: COLORS.textPrimary }}>
                  Objects:{' '}
                  {(coverage?.totalObject ?? cycle.totalObject ?? 0) > 0
                    ? `${coverage?.completedObject ?? cycle.completedObject ?? 0}/${
                        coverage?.totalObject ?? cycle.totalObject ?? 0
                      }`
                    : '—'}
                  {coverage?.completionPercent != null
                    ? ` (${coverage.completionPercent}% completion)`
                    : ''}{' '}
                  · Robot: {cycle.robotId || '—'}
                </Typography>
                {coverage?.coverageAccuracyThreshold != null ? (
                  <Typography sx={{ fontSize: 11, color: COLORS.textSecondary, mt: 0.75 }}>
                    Coverage threshold used: {coverage.coverageAccuracyThreshold}% (tenant-configured
                    accuracy proxy)
                  </Typography>
                ) : null}
                {pack?.contentHash || pack?.pdfHash || pack?.generatingSoftwareVersion ? (
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: COLORS.textSecondary,
                      mt: 1,
                      fontFamily: 'ui-monospace, monospace',
                      wordBreak: 'break-all',
                    }}
                  >
                    {pack.generatingSoftwareVersion
                      ? `Software ${pack.generatingSoftwareVersion}`
                      : null}
                    {pack.contentHash
                      ? `${pack.generatingSoftwareVersion ? ' · ' : ''}Content hash ${String(
                          pack.contentHash
                        ).slice(0, 16)}…`
                      : null}
                    {pack.pdfHash
                      ? ` · PDF SHA ${String(pack.pdfHash).slice(0, 16)}…`
                      : null}
                  </Typography>
                ) : null}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.25 }}>
                  <Chip
                    size="small"
                    label={cycle.status || '—'}
                    sx={{
                      fontWeight: 600,
                      bgcolor: cycle.status === 'Completed' ? '#ecfdf5' : '#fffbeb',
                      color:
                        cycle.status === 'Completed' ? COLORS.accentSuccess : COLORS.accentWarning,
                    }}
                  />
                  {pack?.signed || pack?.signOff?.signerName ? (
                    <Chip
                      size="small"
                      icon={<MdVerified size={14} />}
                      label="Signed"
                      sx={{
                        fontWeight: 600,
                        bgcolor: '#ecfdf5',
                        color: COLORS.accentSuccess,
                        '& .MuiChip-icon': { color: COLORS.accentSuccess },
                      }}
                    />
                  ) : (
                    <Chip size="small" label="Unsigned" variant="outlined" sx={{ fontWeight: 600 }} />
                  )}
                  {pack?.generationSource ? (
                    <Chip
                      size="small"
                      label={pack.generationSource === 'auto' ? 'Auto-generated' : 'On-demand'}
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  ) : null}
                  {pack?.generationStatus === 'generating_failed' ? (
                    <Chip
                      size="small"
                      label="Generation failed"
                      sx={{ fontWeight: 700, bgcolor: '#fef3c7', color: '#b45309' }}
                    />
                  ) : null}
                </Box>
                {formatLatency(pack?.packGenLatencyMs) ? (
                  <Typography sx={{ fontSize: 11, color: COLORS.textSecondary, mt: 1 }}>
                    Pack generated in {formatLatency(pack.packGenLatencyMs)} from cycle end
                    {pack.generatedAt
                      ? ` · ${new Date(pack.generatedAt).toLocaleString('en-GB')}`
                      : ''}
                  </Typography>
                ) : null}
              </Box>
            ) : null}

            <Box
              sx={{
                p: 2,
                borderRadius: `${theme.radius.md}px`,
                bgcolor: '#fff',
                border: `1px solid ${COLORS.borderDefault}`,
              }}
            >
              <Typography sx={sectionLabelSx}>MANUAL CLEAN (REQUIRED PAIRING)</Typography>
              <Typography sx={{ fontSize: 12, color: COLORS.textSecondary, mb: 1.25, lineHeight: 1.45 }}>
                UV-C is an adjunct to manual cleaning. Status:{' '}
                <strong>{(manualClean?.status || 'missing').toUpperCase()}</strong>
              </Typography>
              {allowManualClean ? (
                <>
                  <TextField
                    size="small"
                    label="Cleaner name"
                    required
                    value={cleanerName}
                    onChange={(e) => setCleanerName(e.target.value)}
                    fullWidth
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Role"
                    value={cleanerRole}
                    onChange={(e) => setCleanerRole(e.target.value)}
                    fullWidth
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Protocol / guidance name (optional)"
                    value={protocolName}
                    onChange={(e) => setProtocolName(e.target.value)}
                    fullWidth
                    sx={{ mb: 1 }}
                    helperText="Leave blank to use the facility default environmental clean"
                  />
                  <TextField
                    size="small"
                    label="Notes (optional)"
                    value={cleanNotes}
                    onChange={(e) => setCleanNotes(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    sx={{ mb: 1 }}
                  />
                  {checklistSteps.map((s) => (
                    <FormControlLabel
                      key={s.key}
                      control={
                        <Checkbox
                          checked={Boolean(stepDone[s.key])}
                          onChange={(e) =>
                            setStepDone((prev) => ({ ...prev, [s.key]: e.target.checked }))
                          }
                          size="small"
                        />
                      }
                      label={<Typography sx={{ fontSize: 12 }}>{s.label}</Typography>}
                    />
                  ))}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={Boolean(busy)}
                      onClick={() => handleSaveManualClean(false)}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      {busy === 'manual' ? 'Saving…' : 'Save checklist'}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={Boolean(busy) || !checklistSteps.every((s) => stepDone[s.key])}
                      onClick={() => handleSaveManualClean(true)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        bgcolor: '#0f766e',
                        '&:hover': { bgcolor: '#0d9488' },
                      }}
                    >
                      Mark complete
                    </Button>
                  </Box>
                </>
              ) : (
                checklistSteps.map((s) => (
                  <Typography key={s.key} sx={{ fontSize: 12, mb: 0.5 }}>
                    [{s.completed || stepDone[s.key] ? 'x' : ' '}] {s.label}
                  </Typography>
                ))
              )}
            </Box>

            {coverage?.objects?.length ? (
              <Box
                sx={{
                  p: 2,
                  borderRadius: `${theme.radius.md}px`,
                  bgcolor: '#fff',
                  border: `1px solid ${COLORS.borderDefault}`,
                }}
              >
                <Typography sx={sectionLabelSx}>OBJECT COVERAGE</Typography>
                <Typography sx={{ fontSize: 12, color: COLORS.textSecondary, mb: 1.25, lineHeight: 1.45 }}>
                  States are accuracy proxies (adequate / shadowed / unreachable) — not PASS /
                  MARGINAL / FAIL dose grades. Shadowed and unreachable route to manual wipe
                  follow-up.
                </Typography>
                {coverage.objects.map((obj) => {
                  const chip = statusChipColor(obj.coverageStatus);
                  return (
                    <Box
                      key={obj.objectKey}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1,
                        py: 0.75,
                        borderBottom: `1px solid ${COLORS.borderDefault}`,
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: COLORS.textPrimary }}>
                          {obj.objectName}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: COLORS.textSecondary }}>
                          Accuracy:{' '}
                          {obj.accuracyPercentage != null ? `${obj.accuracyPercentage}%` : '—'}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={COVERAGE_STATUS_LABEL[obj.coverageStatus] || obj.coverageStatus}
                        sx={{
                          fontWeight: 600,
                          bgcolor: chip.bg,
                          color: chip.color,
                          flexShrink: 0,
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            ) : null}

            {motionEvents.length > 0 ? (
              <Box
                sx={{
                  p: 2,
                  borderRadius: `${theme.radius.md}px`,
                  bgcolor: '#fff',
                  border: `1px solid ${COLORS.borderDefault}`,
                }}
              >
                <Typography sx={sectionLabelSx}>MOTION / INTERRUPTIONS</Typography>
                <Typography sx={{ fontSize: 12, color: COLORS.textSecondary, mb: 1.25, lineHeight: 1.45 }}>
                  Cycle interruptions recorded on the robot history (motion detected). These also
                  surface in the exception queue when the cycle aborted.
                </Typography>
                {motionEvents.slice(0, 12).map((m, i) => {
                  const detected = m.detectedTime
                    ? new Date(m.detectedTime).toLocaleString('en-GB')
                    : '—';
                  const outcome = m.abortedTime
                    ? `Aborted ${new Date(m.abortedTime).toLocaleString('en-GB')}`
                    : m.resumeTime
                      ? `Resumed ${new Date(m.resumeTime).toLocaleString('en-GB')}`
                      : '—';
                  return (
                    <Typography
                      key={`motion-${i}`}
                      sx={{ fontSize: 12, color: COLORS.textPrimary, mb: 0.5 }}
                    >
                      {i + 1}. Detected {detected} → {outcome}
                    </Typography>
                  );
                })}
              </Box>
            ) : null}

            {followUpTasks.length > 0 ? (
              <Box
                sx={{
                  p: 2,
                  borderRadius: `${theme.radius.md}px`,
                  bgcolor: '#fff',
                  border: `1px solid ${COLORS.borderDefault}`,
                }}
              >
                <Typography sx={sectionLabelSx}>SHADOWED / UNREACHABLE FOLLOW-UP</Typography>
                {followUpTasks.map((task) => (
                  <Box
                    key={task.id}
                    sx={{
                      py: 1,
                      borderBottom: `1px solid ${COLORS.borderDefault}`,
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: COLORS.textPrimary }}>
                      {task.objectLabel}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: COLORS.textSecondary, mb: 0.75 }}>
                      {task.reason || task.coverageStatus}
                      {task.dueAt
                        ? ` · Due ${new Date(task.dueAt).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}`
                        : ''}
                      {task.slaBreached && ['open', 'acknowledged'].includes(task.status)
                        ? ' · Overdue'
                        : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip size="small" label={task.status} sx={{ fontWeight: 600 }} />
                      <Chip
                        size="small"
                        label={COVERAGE_STATUS_LABEL[task.coverageStatus] || task.coverageStatus}
                        sx={{ fontWeight: 600 }}
                      />
                      {allowFollowUpWrite && task.status === 'open' ? (
                        <Button
                          size="small"
                          disabled={Boolean(busy)}
                          onClick={() => handleFollowUpStatus(task.id, 'acknowledged')}
                          sx={{ textTransform: 'none', fontSize: 12 }}
                        >
                          Acknowledge
                        </Button>
                      ) : null}
                      {allowFollowUpWrite && ['open', 'acknowledged'].includes(task.status) ? (
                        <Button
                          size="small"
                          disabled={Boolean(busy)}
                          onClick={() =>
                            handleFollowUpStatus(task.id, 'completed', 'Manual wipe completed')
                          }
                          sx={{ textTransform: 'none', fontSize: 12 }}
                        >
                          Mark completed
                        </Button>
                      ) : null}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : null}

            <Box
              sx={{
                p: 2,
                borderRadius: `${theme.radius.md}px`,
                bgcolor: '#fff',
                border: `1px solid ${COLORS.borderDefault}`,
              }}
            >
              <Typography sx={sectionLabelSx}>VERIFICATION RESULTS</Typography>
              <Typography sx={{ fontSize: 12, color: COLORS.textSecondary, mb: 1, lineHeight: 1.45 }}>
                Log pack-attached verification (ATP, markers, cultures, observation) against tenant
                thresholds. Results export on the evidence PDF — this is not a standalone
                verification module.
              </Typography>
              {verifications.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: COLORS.textSecondary, mb: 1 }}>
                  No verification logged yet. Add a result below, then regenerate the pack PDF so
                  assessors see the latest evidence.
                </Typography>
              ) : (
                verifications.map((v) => (
                  <Box key={v.id} sx={{ mb: 1.25 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      {METHOD_LABEL[v.method] || v.method} —{' '}
                      {v.passed === true ? 'PASS' : v.passed === false ? 'FAIL' : 'N/A'}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: COLORS.textSecondary }}>
                      Value: {v.rawValue != null ? v.rawValue : v.rawText || '—'} {v.unit || ''} ·
                      Instrument: {v.instrumentId || 'default'}
                      {v.locationLabel ? ` · ${v.locationLabel}` : ''}
                    </Typography>
                    {(v.thresholdPassMax != null || v.thresholdPassMin != null) && (
                      <Typography sx={{ fontSize: 11, color: COLORS.textSecondary }}>
                        Threshold used:{' '}
                        {v.thresholdPassMax != null ? `≤${v.thresholdPassMax}` : ''}
                        {v.thresholdPassMin != null ? ` ≥${v.thresholdPassMin}` : ''} {v.unit || ''}
                      </Typography>
                    )}
                    {v.thresholdSourceCitation ? (
                      <Typography sx={{ fontSize: 11, color: COLORS.textSecondary }}>
                        Source: {v.thresholdSourceCitation}
                      </Typography>
                    ) : null}
                    {v.notes ? (
                      <Typography sx={{ fontSize: 11, color: COLORS.textSecondary }}>
                        Notes: {v.notes}
                      </Typography>
                    ) : null}
                  </Box>
                ))
              )}
              {allowVerification ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                  <TextField
                    select
                    size="small"
                    label="Method"
                    value={verifyMethod}
                    onChange={(e) => setVerifyMethod(e.target.value)}
                  >
                    {METHOD_OPTIONS.map((m) => (
                      <MenuItem key={m.value} value={m.value}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    label="Instrument ID"
                    value={verifyInstrument}
                    onChange={(e) => setVerifyInstrument(e.target.value)}
                  />
                  {verifyMethod === 'direct_observation' ||
                  verifyMethod === 'agar_slide' ||
                  verifyMethod === 'swab_culture' ? (
                    <>
                      <TextField
                        size="small"
                        label={
                          verifyMethod === 'direct_observation'
                            ? 'Observation notes'
                            : 'Result notes / organism'
                        }
                        value={verifyNotes}
                        onChange={(e) => setVerifyNotes(e.target.value)}
                        multiline
                        minRows={2}
                      />
                      <TextField
                        select
                        size="small"
                        label="Pass / fail"
                        value={verifyPassed}
                        onChange={(e) => setVerifyPassed(e.target.value)}
                      >
                        <MenuItem value="">Not set</MenuItem>
                        <MenuItem value="true">Pass</MenuItem>
                        <MenuItem value="false">Fail</MenuItem>
                      </TextField>
                    </>
                  ) : null}
                  {verifyMethod !== 'direct_observation' ? (
                    <TextField
                      size="small"
                      type="number"
                      label="Raw value"
                      value={verifyValue}
                      onChange={(e) => setVerifyValue(e.target.value)}
                      helperText={
                        verifyMethod === 'atp' || verifyMethod === 'fluorescent_marker'
                          ? 'Requires a matching threshold in Hygiene reporting settings'
                          : undefined
                      }
                    />
                  ) : (
                    <TextField
                      size="small"
                      type="number"
                      label="Numeric score (optional)"
                      value={verifyValue}
                      onChange={(e) => setVerifyValue(e.target.value)}
                    />
                  )}
                  <TextField
                    size="small"
                    label="Location / surface"
                    value={verifyLocation}
                    onChange={(e) => setVerifyLocation(e.target.value)}
                    required={verifyMethod !== 'direct_observation'}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={Boolean(busy)}
                    onClick={handleAddVerification}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    {busy === 'verify' ? 'Saving…' : 'Log verification'}
                  </Button>
                </Box>
              ) : null}
            </Box>

            {mapUrl ? (
              <Box
                sx={{
                  p: 2,
                  borderRadius: `${theme.radius.md}px`,
                  bgcolor: '#fff',
                  border: `1px solid ${COLORS.borderDefault}`,
                }}
              >
                <Typography sx={sectionLabelSx}>RESULT MAP</Typography>
                <Box
                  component="img"
                  src={mapUrl}
                  alt="Result map"
                  sx={{
                    width: '100%',
                    maxHeight: 180,
                    objectFit: 'contain',
                    borderRadius: `${theme.radius.md}px`,
                    border: `1px solid ${COLORS.borderDefault}`,
                    bgcolor: COLORS.bgSecondary,
                  }}
                />
              </Box>
            ) : null}

            {standards.length > 0 ? (
              <Box
                sx={{
                  p: 2,
                  borderRadius: `${theme.radius.md}px`,
                  bgcolor: '#fff',
                  border: `1px solid ${COLORS.borderDefault}`,
                }}
              >
                <Typography sx={sectionLabelSx}>OBLIGATION ANCHORS</Typography>
                <Typography sx={{ fontSize: 11, color: COLORS.textSecondary, mb: 1, lineHeight: 1.45 }}>
                  Evidence relevant to these organisational duties — not an endorsement of UV-C by
                  JCI or NABH.
                </Typography>
                {standards.map((s) => (
                  <Typography
                    key={`${s.framework}-${s.code}`}
                    sx={{ fontSize: 12, color: COLORS.textPrimary, mb: 0.75, lineHeight: 1.45 }}
                  >
                    • [{s.framework}] {s.code}: {s.label}
                  </Typography>
                ))}
              </Box>
            ) : null}

            {swabs.length > 0 ? (
              <Box
                sx={{
                  p: 2,
                  borderRadius: `${theme.radius.md}px`,
                  bgcolor: '#fff',
                  border: `1px solid ${COLORS.borderDefault}`,
                }}
              >
                <Typography sx={sectionLabelSx}>LINKED LAB SWABS</Typography>
                {swabs.map((s) => (
                  <Typography key={s.id} sx={{ fontSize: 12, color: COLORS.textPrimary, mb: 0.5 }}>
                    {s.date || '—'} — {s.fileName || s.id}
                    {s.fileLink ? (
                      <>
                        {' '}
                        <a href={s.fileLink} target="_blank" rel="noopener noreferrer">
                          open
                        </a>
                      </>
                    ) : null}
                  </Typography>
                ))}
              </Box>
            ) : null}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {allowGenerate ? (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={Boolean(busy)}
                  onClick={handleGenerate}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: `${theme.radius.md}px`,
                    borderColor: COLORS.borderDefault,
                    color: COLORS.textPrimary,
                  }}
                >
                  {busy === 'generate' ? 'Generating…' : 'Generate PDF'}
                </Button>
              ) : null}
              {allowDownload ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    busy === 'download' ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <MdDownload size={16} />
                    )
                  }
                  disabled={Boolean(busy) || pdfStale}
                  onClick={handleDownload}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: `${theme.radius.md}px`,
                    background: theme.gradients.primary,
                    boxShadow: 'none',
                    '&:hover': { background: theme.gradients.primaryHover, boxShadow: 'none' },
                  }}
                >
                  Download PDF
                </Button>
              ) : null}
            </Box>

            <Divider sx={{ borderColor: COLORS.borderDefault }} />

            {pack?.signOff?.signerName ? (
              <Alert severity="success" sx={{ borderRadius: `${theme.radius.md}px` }}>
                Signed by {pack.signOff.signerName} ({pack.signOff.signerRole}) on{' '}
                {pack.signOff.signedAt
                  ? new Date(pack.signOff.signedAt).toLocaleString('en-GB')
                  : '—'}
                {pack.assessorFeedback ? (
                  <>
                    <br />
                    Assessor feedback
                    {pack.surveyFramework ? ` (${pack.surveyFramework})` : ''}:{' '}
                    {pack.assessorFeedback}
                    {pack.surveyAcceptedAt
                      ? ` · recorded ${new Date(pack.surveyAcceptedAt).toLocaleDateString('en-GB')}`
                      : ''}
                  </>
                ) : null}
              </Alert>
            ) : allowSignOff ? (
              <Box
                sx={{
                  p: 2,
                  borderRadius: `${theme.radius.md}px`,
                  bgcolor: '#fff',
                  border: `1px solid ${COLORS.borderDefault}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary }}>
                  IPC sign-off
                </Typography>
                <TextField
                  size="small"
                  label="Signer name"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Role / designation"
                  value={signerRole}
                  onChange={(e) => setSignerRole(e.target.value)}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Note (optional)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
                <Divider sx={{ borderColor: COLORS.borderDefault }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: COLORS.textPrimary }}>
                  Assessor feedback (optional)
                </Typography>
                <Typography sx={{ fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.45 }}>
                  Capture when an evidence pack was accepted in a real survey. Obligation anchors are
                  organisational-duty references only — not accreditation claims.
                </Typography>
                <TextField
                  select
                  size="small"
                  label="Survey framework"
                  value={surveyFramework}
                  onChange={(e) => setSurveyFramework(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="NABH">NABH</MenuItem>
                  <MenuItem value="JCI">JCI</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  label="Assessor feedback / acceptance note"
                  value={assessorFeedback}
                  onChange={(e) => setAssessorFeedback(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="e.g. Pack accepted as environmental cleaning evidence during survey"
                />
                <Button
                  variant="contained"
                  disabled={Boolean(busy)}
                  onClick={handleSignOff}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: `${theme.radius.md}px`,
                    bgcolor: '#0f766e',
                    '&:hover': { bgcolor: '#0d9488' },
                  }}
                >
                  {busy === 'sign'
                    ? 'Signing…'
                    : pdfStale
                      ? 'Attest (includes latest evidence)'
                      : 'Attest & regenerate PDF'}
                </Button>
              </Box>
            ) : null}
          </>
        )}
      </Box>
    </Drawer>
  );
}

export { downloadPackPdf };
